// Read from window at call time, never imported: a relative import of
// scroll-lock.js loses the ?v= fingerprint and gets cached for a year, so edits
// to it never reach a returning visitor. See the comment in that file.
const lockScroll = (holder) => window.ThemeScrollLock?.lockScroll(holder);
const unlockScroll = (holder) => window.ThemeScrollLock?.unlockScroll(holder);
const trapFocus = (container, initial) => window.ThemeScrollLock?.trapFocus(container, initial);

// Cart drawer. Refreshing re-renders the panel from Shopify's own cart section,
// so prices and totals always come from Shopify rather than being recalculated
// here.
//
// The listeners are split in two on purpose. Anything bound to `document` or to
// this element survives an innerHTML swap, so binding it per render stacks up a
// duplicate every time: after three refreshes a single 'cart:add' fired
// addItems three times and the cart gained three of everything. Only the panel's
// own children — which really are replaced — get rebound per render.
class CartDrawer extends HTMLElement {
  connectedCallback() {
    if (!this.hasSetUp) {
      this.hasSetUp = true;
      this.bindOnce();
    }
    this.bindPanel();
  }

  bindOnce() {
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen()) this.close();
    });

    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:add', (event) => this.addItems(event.detail.items));

    // Delegated, so they keep working across re-renders without rebinding.
    this.addEventListener('click', (event) => {
      const removeBtn = event.target.closest('[data-cart-remove]');
      if (removeBtn) {
        event.preventDefault();
        this.changeLine(removeBtn.dataset.cartRemove, 0);
      }
    });

    this.addEventListener('quantity:change', (event) => {
      const line = event.target.closest('[data-cart-line]');
      if (line) this.changeLine(line.dataset.cartLine, event.detail.value);
    });
  }

  bindPanel() {
    this.panel = this.querySelector('[data-drawer-panel]');
    this.backdrop = this.querySelector('[data-drawer-backdrop]');

    this.querySelectorAll('[data-drawer-close]').forEach((el) =>
      el.addEventListener('click', () => this.close())
    );
    this.backdrop?.addEventListener('click', () => this.close());
  }

  isOpen() {
    return this.panel?.getAttribute('data-open') === 'true';
  }

  open() {
    if (!this.panel) return;
    this.panel.setAttribute('data-open', 'true');
    this.backdrop?.setAttribute('data-open', 'true');
    lockScroll(this);
    // Release any previous trap first: re-rendering while open calls this again,
    // and each trap adds its own keydown listener.
    if (this.releaseFocusTrap) this.releaseFocusTrap();
    this.releaseFocusTrap = trapFocus(this.panel, this.querySelector('[data-drawer-close]'));
  }

  close() {
    // Release the lock before anything can bail out. An early return here left
    // the page unscrollable whenever the panel reference was momentarily
    // missing — for instance if a refresh had failed between open and close.
    unlockScroll(this);
    this.panel?.setAttribute('data-open', 'false');
    this.backdrop?.setAttribute('data-open', 'false');
    if (this.releaseFocusTrap) {
      this.releaseFocusTrap();
      this.releaseFocusTrap = null;
    }
  }

  async addItems(items) {
    // A second click while the first request is still in flight would add the
    // same line twice.
    if (this.busy) return;
    this.busy = true;
    try {
      const response = await fetch(window.themeRoutes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) {
        const error = await response.json();
        document.dispatchEvent(new CustomEvent('cart:error', { detail: error }));
        return;
      }
      // The added line items (with prices, titles, SKUs) — tracking.js reads
      // them to report AddToCart to Meta and Klaviyo.
      const data = await response.json().catch(() => ({}));
      await this.refresh();
      this.open();
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { items: data.items || [] } }));
    } catch (error) {
      document.dispatchEvent(new CustomEvent('cart:error', { detail: error }));
    } finally {
      this.busy = false;
    }
  }

  async changeLine(line, quantity) {
    await fetch(window.themeRoutes.cartChange, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
    });
    await this.refresh();
  }

  async refresh() {
    const url = `${window.themeRoutes.cart}?section_id=cart-drawer`;
    const response = await fetch(url);
    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const newContent = doc.querySelector('cart-drawer');
    if (!newContent) return;

    const wasOpen = this.isOpen();
    this.innerHTML = newContent.innerHTML;
    this.bindPanel();
    if (wasOpen) this.open();

    const rawCount = doc.querySelector('[data-cart-count-value]')?.textContent?.trim();
    const count = Number(rawCount || 0);
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count } }));
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
